import type { ReactNode } from 'react';
import type { Locale } from '@/strings/types';
import { ShellLocaleContext } from './shellLocale';
import {
  IconMenu,
  IconSearch,
  IconPlus,
  IconChevronDown,
  IconHome,
  IconMeetings,
  IconCalendar,
  IconActions,
  IconInsights,
  IconTemplates,
  IconExternal,
  IconMinutes,
  IconTranscript,
  IconAudio,
  IconMic,
  IconCheck,
  IconShare,
  IconProcess,
  IconExport,
  IconClose,
} from './icons';
import './AppShell.css';

/* Which sidebar entry and which content-section entry a given tab lights up.
   Each tab owns its own chrome state so the sidebar and section nav reflect
   what that tab is actually showing, rather than being static decoration. */
export type NavItem = 'home' | 'meetings' | 'calendar' | 'actions' | 'insights' | 'templates';
export type SectionItem = 'minutes' | 'transcript' | 'audio' | 'actions' | 'share';

export interface ShellHeader {
  /** Meeting title shown beside the status dot. */
  title: string;
  /** Project chip text — the app shows "No project" when unassigned. */
  project: string;
  /** ISO-ish display date, already formatted for the locale by the caller. */
  date: string;
}

/* Which page the shell is showing.
   'meeting' is the detail layout: a meeting header spanning the pane, with the
   section nav and content split beneath it.
   'page' is a TOP-LEVEL route — Home, Action Items — which has NEITHER. No
   meeting is open, so there is nothing for a meeting header to name and no
   sections to switch between; rendering an empty one would be inventing chrome
   the app does not have at that route.

   It was called 'home' while tab 3 was its only user. Tab 4 (/actions) is the
   second, and the name had started to describe the one caller rather than the
   layout — `activeNavItem` is what says WHICH top-level route this is. */
export type ShellVariant = 'meeting' | 'page';

interface ShellBase {
  locale: Locale;
  activeNavItem: NavItem;
  /** Replaces the Record well in the sidebar footer — the docked recorder. */
  railRecorder?: ReactNode;
  /**
   * Sits in the top bar beside the hamburger. The stacked band's Record button:
   * below 480 there is no sidebar, so `railRecorder` has nowhere to render.
   */
  topbarRecorder?: ReactNode;
  /**
   * Rendered over the WHOLE SHELL — title bar, top bar, sidebar and pane.
   *
   * Exists for the scripted touch dot, which travels between controls living in
   * different subtrees: the dock in the sidebar, the pop-out in the content
   * area, and — below 480 — the record button in the top bar. It was scoped to
   * `.pnshell__body` until the last of those arrived, which put the top bar out
   * of the dot's reach entirely.
   */
  overlay?: ReactNode;
  children: ReactNode;
}

/* A union, not optional fields: `header` and `activeSection` are REQUIRED by the
   meeting layout and MEANINGLESS to home, and making them merely optional would
   let a caller render a meeting shell with no meeting in it — an empty header
   bar that looks like a loading state. */
export type AppShellProps = ShellBase &
  (
    | { variant?: 'meeting'; activeSection: SectionItem; header: ShellHeader }
    | { variant: 'page'; activeSection?: never; header?: never }
  );

const LOCKUP: Record<Locale, string> = {
  en: '/logos/prinotes-lockup-en-horizontal-colored-on-dark.svg',
  ar: '/logos/prinotes-lockup-ar-horizontal-colored-on-dark.svg',
};

const ICON_MARK = '/logos/prinotes-icon-colored-on-dark.svg';

/* Copy lives inline rather than in web/src/copy/*: this is chrome for a product
   mockup, not site copy. It is never read by a user as prose, and pulling it
   into the copy modules would imply it is subject to the copy-review process. */
const CHROME = {
  en: {
    appTitle: 'PRINOTES App',
    search: 'Search meetings...',
    newBtn: 'New',
    record: 'Record',
    nav: {
      home: 'Home',
      meetings: 'Meetings',
      calendar: 'Manage Calendar',
      actions: 'Action Items',
      insights: 'Insights',
      templates: 'Template Library',
    },
    groupContent: 'CONTENT',
    groupConnect: 'CONNECT',
    section: {
      minutes: 'Minutes Summary',
      transcript: 'Transcript',
      audio: 'Audio',
      actions: 'Action items',
      share: 'Share & Sync',
    },
    process: 'Process',
    exportBtn: 'Export',
    user: { name: 'Reem Alharbi', mail: 'r.alharbi@prinotes.com' },
  },
  ar: {
    appTitle: 'PRINOTES App',
    search: 'البحث في الاجتماعات...',
    newBtn: 'جديد',
    record: 'تسجيل',
    nav: {
      home: 'الرئيسية',
      meetings: 'الاجتماعات',
      calendar: 'التقويم والجدولة',
      actions: 'المهام والإجراءات',
      insights: 'المؤشرات',
      templates: 'مكتبة القوالب',
    },
    groupContent: 'المحتوى',
    groupConnect: 'الاتصال',
    section: {
      minutes: 'ملخص المحضر',
      transcript: 'النص',
      audio: 'الصوت',
      actions: 'المهام والإجراءات',
      share: 'المشاركة والمزامنة',
    },
    process: 'معالجة',
    exportBtn: 'تصدير',
    user: { name: 'ريم الحربي', mail: 'r.alharbi@prinotes.com' },
  },
} as const;

const NAV_ORDER: { id: NavItem; Icon: typeof IconHome }[] = [
  { id: 'home', Icon: IconHome },
  { id: 'meetings', Icon: IconMeetings },
  { id: 'calendar', Icon: IconCalendar },
  { id: 'actions', Icon: IconActions },
  { id: 'insights', Icon: IconInsights },
  { id: 'templates', Icon: IconTemplates },
];

const SECTION_CONTENT: { id: SectionItem; Icon: typeof IconMinutes }[] = [
  { id: 'minutes', Icon: IconMinutes },
  { id: 'transcript', Icon: IconTranscript },
  { id: 'audio', Icon: IconAudio },
  { id: 'actions', Icon: IconCheck },
];

export default function AppShell(props: AppShellProps) {
  /* Deliberately NOT destructuring `variant`/`header`/`activeSection`: they are
     the discriminated union above, and a destructured copy loses the link
     between the tag and the fields it guards. Reading them off `props` inside
     the branch is what lets TypeScript prove `header` is present. */
  const { locale, activeNavItem, railRecorder, topbarRecorder, overlay, children } = props;
  const dir = locale === 'ar' ? 'rtl' : 'ltr';
  const t = CHROME[locale];

  return (
    /* container-type on the frame, not the shell: a container query cannot match
       the element that declares the container, so the queried states have to
       live one level in. Thresholds (760 / 480) are the app's own — see
       meetings-pn.css and shell-chrome.css. */
    <ShellLocaleContext.Provider value={locale}>
    <div className="pnshell-frame">
      {/* data-spotlight dims everything except the recorder surfaces. Derived
          from railRecorder rather than taking a prop of its own: the dock is
          only ever passed by the tab whose subject IS the recorder, so a second
          flag would be a second way to say the same thing — and a way for the
          two to disagree. */}
      <div
        className="pnshell"
        data-pn-theme="dark"
        data-spotlight={Boolean(railRecorder)}
        dir={dir}
      >
        {/* Windows PWA title bar. dir is pinned ltr so the app name stays at the
            physical left and the window controls at the physical right in BOTH
            locales — Windows does not mirror these for our users, and mirroring
            would collide with the Arabic lockup, which sits at the right. */}
        <div className="pnshell__titlebar" dir="ltr">
          <span className="pnshell__titlebar-app">
            <img src={ICON_MARK} alt="" width={16} height={16} />
            {t.appTitle}
          </span>
          <span className="pnshell__titlebar-controls" aria-hidden="true">
            <span className="pnshell__wc pnshell__wc--min" />
            <span className="pnshell__wc pnshell__wc--max" />
            <span className="pnshell__wc pnshell__wc--close" />
          </span>
        </div>

        {/* App top bar */}
        <div className="pnshell__topbar">
          {/* Fixed-width brand zone — the app's "Gmail model" (shell-chrome.css).
              Its width is the sidebar plus the section-nav's text inset, so the
              New button and search start exactly on the content column's text
              edge rather than wherever the lockup happens to end. */}
          <div className="pnshell__brand">
            <span className="pnshell__iconbtn pnshell__hamburger">
              <IconMenu size={20} />
            </span>
            <img className="pnshell__lockup" src={LOCKUP[locale]} alt="Prinotes" />
            <img className="pnshell__mark" src={ICON_MARK} alt="Prinotes" />
          </div>
          {/* Beside the hamburger, before New: the record button is a STATE
              affordance, and grouping it with the brand zone's controls keeps
              the primary action (New) where it is at every other width. */}
          {topbarRecorder}
          <span className="pnshell__new">
            <IconPlus size={16} />
            <span className="pnshell__new-label">{t.newBtn}</span>
            <IconChevronDown size={14} />
          </span>
          <span className="pnshell__search">
            <IconSearch size={15} />
            <span className="pnshell__search-ph">{t.search}</span>
            <kbd className="pnshell__kbd">Ctrl K</kbd>
          </span>
          <span className="pnshell__iconbtn pnshell__searchmini">
            <IconSearch size={18} />
          </span>
        </div>

        <div className="pnshell__body">
          {/* Sidebar */}
          <nav className="pnshell__sidebar">
            <ul className="pnshell__navlist">
              {NAV_ORDER.map(({ id, Icon }) => (
                <li key={id}>
                  <span
                    className="pnshell__navitem"
                    data-active={id === activeNavItem}
                    title={t.nav[id]}
                  >
                    <Icon size={19} />
                    <span className="pnshell__navlabel">{t.nav[id]}</span>
                  </span>
                </li>
              ))}
            </ul>

            <div className="pnshell__sidefoot">
              {/* The docked recorder REPLACES the Record well rather than
                  sitting beside it — in the app the well IS the recorder's idle
                  state, and showing both would be two recorders in one footer.
                  It is suppressed on the rail by CSS: below 760px the sidebar is
                  64px wide and a transport row cannot fit, which is the reason
                  the pop-out exists at all. */}
              {railRecorder ? (
                <div className="pnshell__reccdock">{railRecorder}</div>
              ) : null}
              <div className="pnshell__recordwell" data-replaced={Boolean(railRecorder)}>
                {/* One node, two states. Expanded, only the status dot shows
                    and the label is the affordance. Collapsed, the app swaps to
                    a 36x36 mic button with the dot badged on its corner
                    (.pn-rec-rail__btn, recorder.css:490) — with the label gone,
                    a bare dot names nothing. */}
                <span className="pnshell__record">
                  <span className="pnshell__recicon">
                    <IconMic size={18} />
                    <span className="pnshell__recdot" />
                  </span>
                  <span className="pnshell__navlabel">{t.record}</span>
                </span>
                <span className="pnshell__recpop">
                  <IconExternal size={15} />
                </span>
              </div>

              <div className="pnshell__user">
                <span className="pnshell__avatar">{t.user.name.slice(0, 1)}</span>
                {/* The app's tier chip ("Internal") is deliberately omitted —
                    it exposes account tiering that means nothing to a visitor. */}
                <span className="pnshell__usermeta">
                  <span className="pnshell__username">{t.user.name}</span>
                  <span className="pnshell__usermail">{t.user.mail}</span>
                </span>
              </div>
            </div>
          </nav>

          {/* Detail pane: header spans the full width right of the sidebar, with
              the section nav and content nested beneath it — same as the app. */}
          <div className="pnshell__pane">
            {props.variant === 'page' ? (
              /* A top-level route is one full-bleed column. No dhead, no
                 secnav — see the ShellVariant note. */
              <main className="pnshell__main pnshell__main--page">{children}</main>
            ) : (
              <>
            {/* The app's "Meetings · 17" crumb is dropped here: it costs ~22px
                of vertical in a fixed-height frame and adds nothing to the
                marketing read, since the sidebar already shows where we are. */}
            <header className="pnshell__dhead">
              {/* .pn-meet-dh__block — title and meta stack as ONE flex child so
                  the action cluster centres against both lines, not the title
                  alone. That vertical relationship is what reads as "the app". */}
              <div className="pnshell__dblock">
                <div className="pnshell__dmain">
                  <span className="pnshell__statusdot" />
                  <span className="pnshell__dtitle">{props.header.title}</span>
                </div>
                <span className="pnshell__dmeta">
                  <span>{props.header.project}</span>
                  {/* The calendar glyph is the app's own: PnDetailHeader maps a
                      chip's `date` icon to Icon.Calendar, so the date never
                      appears here unlabelled. It is DELIBERATELY NOT mirrored
                      under RTL — a calendar is an object, not a direction, and
                      pn-mirror is for glyphs that point. */}
                  <span className="pnshell__dmeta-date">
                    <IconCalendar size={12} />
                    {props.header.date}
                  </span>
                </span>
              </div>
              <span className="pnshell__dactions">
                <span className="pnshell__chip">
                  <IconProcess size={14} />
                  <span className="pnshell__chip-label">{t.process}</span>
                  <IconChevronDown size={13} />
                </span>
                <span className="pnshell__chip">
                  <IconExport size={14} />
                  <span className="pnshell__chip-label">{t.exportBtn}</span>
                  <IconChevronDown size={13} />
                </span>
                <span className="pnshell__chip pnshell__chip--icon">
                  <IconClose size={14} />
                </span>
              </span>
            </header>

            <div className="pnshell__dbody">
              <nav className="pnshell__secnav">
                <span className="pnshell__grouplabel">{t.groupContent}</span>
                <ul className="pnshell__seclist">
                  {SECTION_CONTENT.map(({ id, Icon }) => (
                    <li key={id}>
                      <span
                        className="pnshell__secitem"
                        data-active={id === props.activeSection}
                        title={t.section[id]}
                      >
                        <Icon size={16} />
                        <span className="pnshell__seclabel">{t.section[id]}</span>
                      </span>
                    </li>
                  ))}
                </ul>
                <span className="pnshell__grouplabel">{t.groupConnect}</span>
                <ul className="pnshell__seclist">
                  <li>
                    <span
                      className="pnshell__secitem"
                      data-active={props.activeSection === 'share'}
                      title={t.section.share}
                    >
                      <IconShare size={16} />
                      <span className="pnshell__seclabel">{t.section.share}</span>
                    </span>
                  </li>
                </ul>
              </nav>

              <main className="pnshell__main">{children}</main>
            </div>
              </>
            )}
          </div>
        </div>

        {/* Outside .pnshell__body on purpose — see the `overlay` note. The dot
            must be able to reach the top bar, which is the body's sibling. */}
        {overlay}
      </div>
    </div>
    </ShellLocaleContext.Provider>
  );
}
